import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Card, Alert, Loader, Badge, Button } from '@dreamtree-org/twreact-ui';
import { File, Folder, Search, RefreshCw } from 'lucide-react';
import Can from '@rbac/Can';
import { useGitApp, errText } from './useGitApp';

const MAX_VISIBLE = 500;

/**
 * Default work branch for a staging session. Mirrors the `vibe/` convention in
 * GitApp.onCommitAction — never the base branch, because commitStaged pushes straight to
 * whatever this names.
 */
const defaultWorkBranch = () => `edit/${new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '')}`;

const prettySize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

/**
 * Repo tree + file viewer.
 *
 * The tree arrives flat and recursive from the GitHub API (already capped server-side),
 * so filtering happens client-side — no round trip per keystroke. Only the first
 * MAX_VISIBLE matches render: a monorepo tree is tens of thousands of entries and
 * mounting them all locks the tab.
 */
export default function FileExplorer({ appId, branch }) {
  const { act } = useGitApp(appId);

  const [tree, setTree] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');

  const [file, setFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);

  // Editing state. `draft` is null until the user chooses to edit, so viewing stays cheap and
  // an accidental keystroke can't stage anything.
  const [draft, setDraft] = useState(null);
  const [workBranch, setWorkBranch] = useState(defaultWorkBranch);
  const [staging, setStaging] = useState(false);
  const [staged, setStaged] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await act('tree', branch ? { ref: branch } : {});
      setTree(d.tree || []);
      setTruncated(!!d.truncated);
      setErr('');
    } catch (ex) {
      setErr(errText(ex, 'Could not load the repository tree'));
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, [act, branch]);

  useEffect(() => { load(); }, [load]);

  const files = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const blobs = tree.filter((t) => t.type === 'blob');
    return (needle ? blobs.filter((t) => t.path.toLowerCase().includes(needle)) : blobs).slice(0, MAX_VISIBLE);
  }, [tree, q]);

  const open = async (path) => {
    setFileLoading(true);
    setErr('');
    setDraft(null);   // switching files abandons an unstaged draft rather than carrying it over
    setStaged('');
    try {
      setFile(await act('file', { path, ...(branch ? { ref: branch } : {}) }));
    } catch (ex) {
      setErr(errText(ex, 'Could not load that file'));
      setFile(null);
    } finally {
      setFileLoading(false);
    }
  };

  const stage = async () => {
    if (!file || draft == null) return;
    setStaging(true);
    setErr('');
    try {
      await act('stageEdit', {
        path: file.path,
        content: draft,
        workBranch: workBranch.trim(),
        ...(branch ? { baseBranch: branch } : {}),
      });
      setStaged(`Staged onto ${workBranch.trim()} — review and push it from Source Control.`);
      setDraft(null);
    } catch (ex) {
      setErr(errText(ex, 'Could not stage that edit'));
    } finally {
      setStaging(false);
    }
  };

  if (loading) return <Loader text="Loading repository tree…" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <Card className="p-3 lg:col-span-2 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50 z-10" />
            <Input className="pl-8" placeholder="Filter files…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button type="button" className="opacity-50 hover:opacity-100" onClick={load} title="Reload tree">
            <RefreshCw size={15} />
          </button>
        </div>

        <div className="flex items-center gap-2 text-[11px] opacity-60">
          <Folder size={12} /> {tree.length} entries
          {truncated ? <Badge variant="warning">truncated</Badge> : null}
        </div>

        <div className="max-h-[60vh] overflow-y-auto divide-y divide-secondary-100 dark:divide-secondary-800">
          {files.length === 0 ? (
            <p className="text-sm opacity-50 p-3">No files match.</p>
          ) : files.map((t) => (
            <button
              key={t.path}
              type="button"
              onClick={() => open(t.path)}
              className={`w-full text-left px-2 py-1.5 text-xs font-mono flex items-center gap-2 hover:bg-secondary-50 dark:hover:bg-secondary-800 ${file?.path === t.path ? 'bg-primary-50 text-primary-700' : ''}`}
            >
              <File size={12} className="shrink-0 opacity-50" />
              <span className="truncate">{t.path}</span>
              <span className="ml-auto opacity-50 shrink-0">{prettySize(t.size)}</span>
            </button>
          ))}
        </div>
        {files.length === MAX_VISIBLE ? (
          <p className="text-[11px] opacity-50">Showing the first {MAX_VISIBLE} matches — narrow the filter to see more.</p>
        ) : null}
      </Card>

      <Card className="p-3 lg:col-span-3">
        {err ? <Alert variant="error" title="File">{err}</Alert> : null}
        {fileLoading && <Loader text="Loading file…" />}
        {!fileLoading && !file && !err && <p className="text-sm opacity-50 p-4">Select a file to view it.</p>}
        {!fileLoading && file ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="font-mono text-xs break-all">{file.path}</span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] opacity-50">{prettySize(file.size)}</span>
                <Can module="development" action="commit">
                  {draft == null ? (
                    <Button size="xs" variant="outline" onClick={() => { setDraft(file.content || ''); setStaged(''); }}>Edit</Button>
                  ) : (
                    <Button size="xs" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
                  )}
                </Can>
              </div>
            </div>

            {staged ? <Alert variant="success" title="Change staged">{staged}</Alert> : null}

            {draft == null ? (
              <pre className="text-[11px] font-mono bg-secondary-50 dark:bg-secondary-800 rounded p-3 overflow-auto max-h-[60vh] whitespace-pre">
                {file.content}
              </pre>
            ) : (
              <div className="space-y-2">
                {/* A plain textarea on purpose — a syntax-highlighting editor is a large
                    dependency for a tab whose job is small, targeted fixes. */}
                <textarea
                  className="w-full h-[55vh] text-[11px] font-mono bg-secondary-50 dark:bg-secondary-800 rounded p-3 whitespace-pre overflow-auto"
                  value={draft}
                  spellCheck={false}
                  onChange={(e) => setDraft(e.target.value)}
                />
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[16rem]">
                    <Input
                      label="Work branch"
                      value={workBranch}
                      onChange={(e) => setWorkBranch(e.target.value)}
                    />
                  </div>
                  <Button onClick={stage} disabled={staging || !workBranch.trim() || draft === file.content}>
                    {staging ? 'Staging…' : 'Stage change'}
                  </Button>
                </div>
                {/* The commit path never touches the base branch — say so here, where someone
                    is about to press the button, not only in the model's comments. */}
                <p className="text-[11px] opacity-60">
                  Staged edits are held in this app until you push them from Source Control, which
                  commits to the work branch above — never to {branch || 'the base branch'}.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
